import {
  Choice,
  Order,
  RequirementFeAction,
  RequirementFeActionCondition,
  ReturnFullProduct,
} from './types';
import { computeReturnProductTotal } from './returnPricing';
import {
  gatewayEquals,
  gatewayIn,
  gatewayContains,
  ruleListValues,
  ruleNumber,
} from './ruleOperators';

// #128: the return methods the server will accept for the products being
// returned, from the SET_RETURN_METHODS rules sent in fe_actions (rules with a
// product condition, i.e. on the return reason). Mirrors return-api (#271):
// - every matching rule allows only its method_ids (SetReturnMethodsAction
//   checks each one at submit), so the allowed set is their INTERSECTION; an
//   empty intersection refuses every method;
// - one product condition matches when ANY returned product matches it, ANDed
//   with the rule's order conditions;
// - method_ids are compared as the JSON text of each element with the method
//   id (JsonNode.toString()): 11 matches method 11, "11" never does.
// The portal is advisory: a rule it cannot evaluate exactly (a return.* or
// line_item condition, two product conditions, a date / boolean / unknown
// operator or field, malformed params, a fact it does not know) is skipped and
// the server stays the judge. It never hides a method on a guess.
// Returns null when no rule applies (every method allowed).

type Verdict = boolean | undefined; // undefined: cannot be decided here

const PRODUCT_FACTS: Record<
  string,
  (row: ReturnFullProduct, reasons: Choice[]) => unknown
> = {
  'return_reason.name': (row, reasons) =>
    reasons.find(reason => String(reason.value) === String(row.reason))?.name,
  quantity: row => row.quantityReturn,
  description: row => row.details,
  currency: row => row.currency,
  // the server sets the product price (its refund) before the rules run
  price: row => computeReturnProductTotal(row),
};

const SUPPORTED_CODES = new Set([
  'EQUALS',
  'NOT_EQUALS',
  'IN',
  'NOT_IN',
  'CONTAINS',
  'NOT_CONTAINS',
  'GREATER_THAN',
  'GREATER_THAN_OR_EQUAL',
  'LESS_THAN',
  'LESS_THAN_OR_EQUAL',
  'BETWEEN',
]);

const pathOf = (condition: RequirementFeActionCondition): string[] => {
  const keys: string[] = [];
  for (let a = condition.attribute; a; a = a.parent as typeof a) {
    keys.unshift(a.attribute_key);
  }
  return keys;
};

// MVEL equality: STRING operands are quoted (strict text equality, "02" is not
// "2"); NUMBER operands are compared as numbers
const equalsOperand = (
  fact: unknown,
  operand: string,
  valueType: string
): Verdict => {
  if (valueType === 'NUMBER') {
    const a = ruleNumber(fact);
    const b = ruleNumber(operand);
    return a === undefined || b === undefined ? undefined : a === b;
  }
  if (valueType === 'STRING') return String(fact) === operand;
  return undefined;
};

const compareNumbers = (
  fact: unknown,
  operand: string,
  test: (a: number, b: number) => boolean
): Verdict => {
  const a = ruleNumber(fact);
  const b = ruleNumber(operand);
  return a === undefined || b === undefined ? undefined : test(a, b);
};

const evaluate = (
  condition: RequirementFeActionCondition,
  fact: unknown
): Verdict => {
  if (fact === undefined || fact === null || typeof fact === 'object') {
    return undefined;
  }
  const operand = condition.value ?? '';
  const path = pathOf(condition);
  if (
    path.length === 2 &&
    path[0] === 'order' &&
    path[1] === 'payment_method'
  ) {
    switch (condition.operator.code) {
      case 'EQUALS':
        return gatewayEquals(fact, operand);
      case 'NOT_EQUALS':
        return !gatewayEquals(fact, operand);
      case 'IN':
        return gatewayIn(fact, operand);
      case 'NOT_IN':
        return !gatewayIn(fact, operand);
      case 'CONTAINS':
        return gatewayContains(fact, operand);
      case 'NOT_CONTAINS':
        return !gatewayContains(fact, operand);
    }
  }
  const valueType = (condition.operator.value_type ?? '').toUpperCase();
  switch (condition.operator.code) {
    case 'EQUALS':
      return equalsOperand(fact, operand, valueType);
    case 'NOT_EQUALS': {
      const equal = equalsOperand(fact, operand, valueType);
      return equal === undefined ? undefined : !equal;
    }
    case 'IN':
    case 'NOT_IN': {
      const verdicts = ruleListValues(operand).map(element =>
        equalsOperand(fact, element, valueType)
      );
      if (verdicts.includes(undefined)) return undefined;
      const found = verdicts.includes(true);
      return condition.operator.code === 'IN' ? found : !found;
    }
    case 'CONTAINS':
    case 'NOT_CONTAINS': {
      // MVEL String.contains: a substring test on a text fact
      if (typeof fact !== 'string') return undefined;
      const found = fact.includes(operand);
      return condition.operator.code === 'CONTAINS' ? found : !found;
    }
    case 'GREATER_THAN':
      return compareNumbers(fact, operand, (a, b) => a > b);
    case 'GREATER_THAN_OR_EQUAL':
      return compareNumbers(fact, operand, (a, b) => a >= b);
    case 'LESS_THAN':
      return compareNumbers(fact, operand, (a, b) => a < b);
    case 'LESS_THAN_OR_EQUAL':
      return compareNumbers(fact, operand, (a, b) => a <= b);
    case 'BETWEEN': {
      const bounds = operand.split(',');
      if (bounds.length !== 2) return undefined;
      const atLeast = compareNumbers(fact, bounds[0], (a, b) => a >= b);
      const atMost = compareNumbers(fact, bounds[1], (a, b) => a <= b);
      return atLeast === undefined || atMost === undefined
        ? undefined
        : atLeast && atMost;
    }
    default:
      return undefined;
  }
};

const orderFact = (order: Order, keys: string[]): unknown =>
  keys.reduce<unknown>(
    (value, key) =>
      value !== null && typeof value === 'object'
        ? (value as Record<string, unknown>)[key]
        : undefined,
    order
  );

// the method_ids of a rule as the server compares them, or undefined when its
// params cannot be read (the rule is then skipped)
const methodIdsOf = (action: RequirementFeAction): string[] | undefined => {
  let params: unknown;
  try {
    params = JSON.parse(action.rule_action.action_params);
  } catch {
    return undefined;
  }
  if (params === null || typeof params !== 'object' || Array.isArray(params)) {
    return undefined;
  }
  const ids = (params as Record<string, unknown>).method_ids;
  if (ids === undefined) return [];
  if (!Array.isArray(ids)) return undefined;
  return ids.map(id => JSON.stringify(id));
};

const ruleMatches = (
  action: RequirementFeAction,
  products: ReturnFullProduct[],
  reasons: Choice[],
  order: Order,
  reasonUnknown: boolean
): Verdict => {
  const conditions = action.conditions.map(condition => ({
    condition,
    path: pathOf(condition),
  }));
  if (
    conditions.some(
      ({ condition }) => !SUPPORTED_CODES.has(condition.operator.code)
    )
  ) {
    return undefined;
  }
  const product = conditions.filter(({ path }) => path[0] === 'product');
  const others = conditions.filter(({ path }) => path[0] !== 'product');
  if (product.length !== 1 || others.some(({ path }) => path[0] !== 'order')) {
    return undefined;
  }
  const factName = product[0].path.slice(1).join('.');
  // #149: the reason of a withdrawal return is the server's system reason,
  // unknown to the portal: a rule on it is skipped (the server judges it)
  if (reasonUnknown && factName.startsWith('return_reason')) return undefined;
  const productFact = PRODUCT_FACTS[factName];
  if (!productFact) return undefined;

  for (const { condition, path } of others) {
    const verdict = evaluate(condition, orderFact(order, path.slice(1)));
    if (verdict !== true) return verdict;
  }
  // a product whose fact is unknown is never taken as a match
  return products.some(
    row => evaluate(product[0].condition, productFact(row, reasons)) === true
  );
};

export const evaluateReturnMethodRules = (
  feActions: RequirementFeAction[],
  products: ReturnFullProduct[],
  reasons: Choice[],
  order: Order,
  options: { reasonUnknown?: boolean } = {}
): { allowedIds: Set<string> | null; matchedRuleIds: number[] } => {
  const matchedRuleIds: number[] = [];
  const matched: string[][] = [];
  for (const action of feActions) {
    if (action.rule_action?.action_type !== 'SET_RETURN_METHODS') continue;
    const ids = methodIdsOf(action);
    if (ids === undefined) continue;
    if (
      ruleMatches(
        action,
        products,
        reasons,
        order,
        options.reasonUnknown === true
      ) === true
    ) {
      matched.push(ids);
      matchedRuleIds.push(action.id);
    }
  }
  return {
    allowedIds:
      matched.length === 0
        ? null
        : new Set(
            matched[0].filter(id => matched.every(ids => ids.includes(id)))
          ),
    matchedRuleIds,
  };
};

// Preserve the existing set-only API; attribution is collected in the same pass.
export const allowedReturnMethodIds = (
  ...args: Parameters<typeof evaluateReturnMethodRules>
): Set<string> | null => evaluateReturnMethodRules(...args).allowedIds;
